import React, {useState} from "react";
import {Typography} from "@material-ui/core";
import {Timeline} from "@material-ui/lab";
import {computeProgress, computeTimeRemaining} from "./stage_calculations";
import {StageTimelineItem, PendingTimelineItem} from "./timeline_items";

export const TIMELINE_STAGES = {
  parsing: [4, 0, 7, 8],
  classifying: [1],
};

export const TIMELINE_STAGES_ORDER = ["parsing", "classifying"];

const STAGE_CONFIG = {
  parsing: {
    title: "Parsing the Document",
    description: "The structure and content of the document is being extracted",
  },
  classifying: {
    title: "Classifying Clauses",
    description: "The clauses are being classified by the AI",
  },
};

export const getTimelineStage = loadState => {
  return Object.entries(TIMELINE_STAGES).find(([, states]) =>
    states.includes(loadState),
  )?.[0];
};

export const UploadTimeline = ({
  document,
  refreshTime,
  isWebapp = false,
  collapse = false,
}) => {
  const allLoadStates = document.concat_load_state ?? [document.load_state];

  const computeIsStageComplete = stageName => {
    const stageIdx = TIMELINE_STAGES_ORDER.indexOf(stageName);
    const isActive = allLoadStates.some(ls =>
      TIMELINE_STAGES[stageName].includes(ls),
    );
    return (
      !isActive &&
      allLoadStates.every(ls => {
        const lsStage = getTimelineStage(ls);
        return lsStage
          ? TIMELINE_STAGES_ORDER.indexOf(lsStage) > stageIdx
          : [2, 3, 9].includes(ls);
      })
    );
  };

  const [visuallyCompleteStages, setVisuallyCompleteStages] = useState<
    Set<string>
  >(() => new Set(TIMELINE_STAGES_ORDER.filter(computeIsStageComplete)));

  const onVisuallyComplete = stageName =>
    setVisuallyCompleteStages(prev => new Set([...prev, stageName]));

  const isUnlocked = stageName => {
    const idx = TIMELINE_STAGES_ORDER.indexOf(stageName);
    return (
      idx === 0 || visuallyCompleteStages.has(TIMELINE_STAGES_ORDER[idx - 1])
    );
  };

  const stageProps = stageName => {
    const stageIdx = TIMELINE_STAGES_ORDER.indexOf(stageName);
    const isActive = allLoadStates.some(ls =>
      TIMELINE_STAGES[stageName].includes(ls),
    );
    const isComplete =
      !isActive &&
      allLoadStates.every(ls => {
        const lsStage = getTimelineStage(ls);
        return lsStage
          ? TIMELINE_STAGES_ORDER.indexOf(lsStage) > stageIdx
          : [2, 3, 9].includes(ls);
      });
    const percentComplete = isActive
      ? computeProgress(document, stageName)
      : undefined;
    const timeRemaining = isActive
      ? computeTimeRemaining(document, stageName)
      : undefined;
    return {isActive, isComplete, percentComplete, timeRemaining};
  };

  const activeStage = TIMELINE_STAGES_ORDER.find(s => stageProps(s).isActive);

  if (collapse) {
    const stageName = activeStage ?? TIMELINE_STAGES_ORDER[0];
    return (
      <Timeline style={{padding: 0, marginBottom: 0}}>
        <StageTimelineItem
          {...STAGE_CONFIG[stageName]}
          refreshTime={refreshTime}
          isUnlocked={true}
          onVisuallyComplete={() => onVisuallyComplete(stageName)}
          connector={false}
          {...stageProps(stageName)}
        />
      </Timeline>
    );
  }

  return (
    <div style={{padding: "1em"}}>
      {!isWebapp && (
        <>
          <Typography variant="h5" style={{textAlign: "center"}}>
            Document Upload
          </Typography>
          <Typography
            variant="subtitle1"
            style={{color: "#888888ff", textAlign: "center"}}
          >
            {document.name}
          </Typography>
        </>
      )}
      <Timeline style={{padding: 0, marginBottom: 0}}>
        {TIMELINE_STAGES_ORDER.map(stageName => (
          <StageTimelineItem
            key={stageName}
            {...STAGE_CONFIG[stageName]}
            refreshTime={refreshTime}
            isUnlocked={isUnlocked(stageName)}
            onVisuallyComplete={() => onVisuallyComplete(stageName)}
            {...stageProps(stageName)}
          />
        ))}
        <PendingTimelineItem title="Processing Issues" connector={false} />
      </Timeline>
    </div>
  );
};
